import { createAction, createAsyncThunk, createReducer } from '@reduxjs/toolkit';
import { API, SCOPE_FEDERAL_AGENTS } from '../../constants';
import { FEDERAL_AGENTS } from '../../constants/api';
import { application } from '../../services/application';
import { INITIAL_LIST_STATE } from '../helpers/common';
import { extendBuilderWithListActions, generateListActions } from '../helpers/listActionsHelpers';
import { onReject } from '../helpers/sharedCases';

const INITIAL_CONTRACT_AWARDS_STATE = {
  ...INITIAL_LIST_STATE,
  filters: {
    ...(INITIAL_LIST_STATE.filters || {}),
    text: '',
    fields: {},
  },
  order: ['signedDate', 'desc'],
  pagination: {
    ...(INITIAL_LIST_STATE.pagination || {}),
    page: 1,
    perPage: 10,
  },
  allItems: [],
};

const INITIAL_CONTRACT_OPPORTUNITIES_STATE = {
  ...INITIAL_LIST_STATE,
  filters: {
    ...(INITIAL_LIST_STATE.filters || {}),
    text: '',
    fields: {},
  },
  params: {
    ...(INITIAL_LIST_STATE.params || {}),
    quickFilter: 'active',
  },
  order: ['postedDate', 'desc'],
  pagination: {
    ...(INITIAL_LIST_STATE.pagination || {}),
    page: 1,
    perPage: 10,
  },
  counts: null,
  allItems: [],
};

export const INITIAL_STATE = {
  item: null,
  pending: true,

  // Keep the exact id used to load the detail page.
  // This matters because the route lookup id may be email/_id while item.id may be a different generated UUID.
  lookupId: null,

  contractAwards: INITIAL_CONTRACT_AWARDS_STATE,
  contractOpportunities: INITIAL_CONTRACT_OPPORTUNITIES_STATE,

  naicsCodes: INITIAL_LIST_STATE,
  colleagues: { ...INITIAL_LIST_STATE, pagination: { page: 1, perPage: 3 } },
  opportunities: INITIAL_LIST_STATE,
};

function getArgs(payload) {
  return Array.isArray(payload) ? payload : [payload];
}

function getEventValue(arg) {
  if (arg?.target?.value !== undefined) return arg.target.value;
  if (arg?.currentTarget?.value !== undefined) return arg.currentTarget.value;

  return undefined;
}

function extractPage(payload) {
  const args = getArgs(payload);

  const objectValue = args.find(
    (arg) => arg && typeof arg === 'object' && Object.prototype.hasOwnProperty.call(arg, 'page')
  );
  const eventValue = args.map(getEventValue).find((value) => value !== undefined);
  const rawValue =
    objectValue?.page ??
    eventValue ??
    args.find((arg) => typeof arg === 'number' || (typeof arg === 'string' && !Number.isNaN(Number(arg))));

  const page = Number(rawValue);

  return Number.isFinite(page) && page > 0 ? page : 1;
}

function extractPerPage(payload) {
  const args = getArgs(payload);

  const objectValue = args.find(
    (arg) =>
      arg &&
      typeof arg === 'object' &&
      (Object.prototype.hasOwnProperty.call(arg, 'perPage') ||
        Object.prototype.hasOwnProperty.call(arg, 'rowsPerPage') ||
        arg?.target?.value !== undefined)
  );

  const eventValue = args.map(getEventValue).find((value) => value !== undefined);
  const rawValue =
    objectValue?.perPage ??
    objectValue?.rowsPerPage ??
    eventValue ??
    args.find((arg) => typeof arg === 'number' || (typeof arg === 'string' && !Number.isNaN(Number(arg))));

  const perPage = Number(rawValue);

  return Number.isFinite(perPage) && perPage > 0 ? perPage : 10;
}

function extractSearchText(payload) {
  const args = getArgs(payload);
  const eventValue = args.map(getEventValue).find((value) => value !== undefined);

  if (eventValue !== undefined) {
    return eventValue;
  }

  const objectValue = args.find(
    (arg) =>
      arg &&
      typeof arg === 'object' &&
      (Object.prototype.hasOwnProperty.call(arg, 'text') ||
        Object.prototype.hasOwnProperty.call(arg, 'value') ||
        Object.prototype.hasOwnProperty.call(arg, 'searchText'))
  );

  if (objectValue) {
    return objectValue.text ?? objectValue.searchText ?? objectValue.value ?? '';
  }

  const stringValues = args.filter((arg) => typeof arg === 'string');

  // SimpleSearchField can call either:
  // onValueChange(value)
  // onValueChange(name, value)
  // This takes the last string so we do not accidentally search for the field name.
  return stringValues.length ? stringValues[stringValues.length - 1] : '';
}

function extractOrder(payload, defaultOrder) {
  if (Array.isArray(payload) && payload.length === 2 && typeof payload[0] === 'string') {
    return payload;
  }

  const args = getArgs(payload);

  const objectValue = args.find(
    (arg) =>
      arg &&
      typeof arg === 'object' &&
      (Array.isArray(arg.order) || (arg.field && arg.direction) || (arg.value && arg.direction))
  );

  if (objectValue?.order) {
    return objectValue.order;
  }

  if (objectValue?.field && objectValue?.direction) {
    return [objectValue.field, objectValue.direction];
  }

  if (objectValue?.value && objectValue?.direction) {
    return [objectValue.value, objectValue.direction];
  }

  const order = args.find((arg) => Array.isArray(arg) && arg.length === 2);

  return order || defaultOrder;
}

function normalizeListPayload(payload) {
  if (Array.isArray(payload)) {
    return {
      count: payload.length,
      items: payload,
      allItems: payload,
    };
  }

  const items = payload?.items || [];
  const allItems = payload?.allItems || items;

  return {
    count: Number(payload?.count || 0),
    items,
    allItems,
    counts: payload?.counts || null,
  };
}

function getRequestParamValue(params, key) {
  if (params && Object.prototype.hasOwnProperty.call(params, key)) {
    return params[key];
  }

  return undefined;
}

function getContractAwardsRequestParams(state, params = {}) {
  const current = state.contractAwards || INITIAL_CONTRACT_AWARDS_STATE;
  const currentFilters = current.filters || {};
  const nestedParams = params.contractAwards || {};

  return {
    text: currentFilters.text || '',
    filters: {
      fields: currentFilters.fields || {},
    },
    order: current.order || ['signedDate', 'desc'],
    pagination: current.pagination || { page: 1, perPage: 10 },
    ...nestedParams,
  };
}

function getContractOpportunitiesRequestParams(state, params = {}) {
  const current = state.contractOpportunities || INITIAL_CONTRACT_OPPORTUNITIES_STATE;
  const currentFilters = current.filters || {};
  const currentParams = current.params || {};
  const nestedParams = params.contractOpportunities || params.opportunities || {};

  const directParams = {
    text: getRequestParamValue(params, 'text'),
    filters: getRequestParamValue(params, 'filters'),
    quickFilter: getRequestParamValue(params, 'quickFilter'),
    order: getRequestParamValue(params, 'order'),
    pagination: getRequestParamValue(params, 'pagination'),
  };

  Object.keys(directParams).forEach((key) => {
    if (directParams[key] === undefined) delete directParams[key];
  });

  return {
    text: currentFilters.text || '',
    filters: {
      fields: currentFilters.fields || {},
    },
    quickFilter: currentParams.quickFilter || 'active',
    order: current.order || ['postedDate', 'desc'],
    pagination: current.pagination || { page: 1, perPage: 10 },
    ...directParams,
    ...nestedParams,
  };
}

function getFederalAgentId(state, params = {}) {
  return params.id || state.lookupId || state.item?.email || state.item?._id || state.item?.id;
}

const setContractAwardsPagination = createAction('federalAgents/details/contractAwards/setPagination');
const setContractAwardsFilters = createAction('federalAgents/details/contractAwards/setFilters');
const setContractAwardsOrder = createAction('federalAgents/details/contractAwards/setOrder');

const setContractOpportunitiesPagination = createAction('federalAgents/details/contractOpportunities/setPagination');
const setContractOpportunitiesFilters = createAction('federalAgents/details/contractOpportunities/setFilters');
const setContractOpportunitiesOrder = createAction('federalAgents/details/contractOpportunities/setOrder');
const setContractOpportunitiesParams = createAction('federalAgents/details/contractOpportunities/setParams');

const naicsCodesListAtions = generateListActions({
  scope: `${SCOPE_FEDERAL_AGENTS}/naicsCodeList`,
  apiMethod: { GET_LIST: API.FEDERAL_AGENTS.GET_NAICS_CODES },
  getStore: (store) => store.federalAgentDetails.naicsCodes,
});

const colleaguesListAtions = generateListActions({
  scope: `${SCOPE_FEDERAL_AGENTS}/colleagues`,
  apiMethod: { GET_LIST: API.FEDERAL_AGENTS.GET_COLLEAGUES },
  getStore: (store) => store.federalAgentDetails.colleagues,
});

const opportunitiesListAtions = generateListActions({
  scope: `${SCOPE_FEDERAL_AGENTS}/opportunities`,
  apiMethod: { GET_LIST: API.FEDERAL_AGENTS.GET_POSTED_OPPORTUNITIES },
  getStore: (store) => store.federalAgentDetails.opportunities,
});

export const fetchFederalAgent = createAsyncThunk('federalAgents/findOne', async (params) => {
  return application.call(FEDERAL_AGENTS.FIND_ONE, params);
});

const fetchFederalAgentContractAwards = createAsyncThunk(
  'federalAgents/details/contractAwards/findOne',
  async (params = {}, { getState }) => {
    const state = getState().federalAgentDetails;
    const id = getFederalAgentId(state, params);

    if (!id) {
      return { count: 0, items: [], allItems: state.contractAwards?.allItems || [] };
    }

    const response = await application.call(FEDERAL_AGENTS.FIND_ONE, {
      id,
      contractAwards: getContractAwardsRequestParams(state, params),
    });

    return normalizeListPayload(response?.contractAwards);
  }
);

const fetchFederalAgentContractOpportunities = createAsyncThunk(
  'federalAgents/details/contractOpportunities/findOne',
  async (params = {}, { getState }) => {
    const state = getState().federalAgentDetails;
    const id = getFederalAgentId(state, params);

    if (!id) {
      return {
        count: 0,
        items: [],
        allItems: state.contractOpportunities?.allItems || [],
        counts: state.contractOpportunities?.counts || null,
      };
    }

    const response = await application.call(FEDERAL_AGENTS.FIND_ONE, {
      id,
      contractOpportunities: getContractOpportunitiesRequestParams(state, params),
    });

    return normalizeListPayload(response?.contractOpportunities);
  }
);

const changeContractAwardsPage = (payload) => async (dispatch) => {
  dispatch(setContractAwardsPagination({ page: extractPage(payload) }));
  return dispatch(fetchFederalAgentContractAwards());
};

const changeContractAwardsRowPerPage = (payload) => async (dispatch) => {
  dispatch(
    setContractAwardsPagination({
      page: 1,
      perPage: extractPerPage(payload),
    })
  );

  return dispatch(fetchFederalAgentContractAwards());
};

const changeContractAwardsSearchText = (payload) => async (dispatch) => {
  dispatch(setContractAwardsFilters({ text: extractSearchText(payload) }));
  dispatch(setContractAwardsPagination({ page: 1 }));

  return dispatch(fetchFederalAgentContractAwards());
};

const changeContractAwardsOrder = (payload) => async (dispatch) => {
  dispatch(setContractAwardsOrder(extractOrder(payload, ['signedDate', 'desc'])));
  dispatch(setContractAwardsPagination({ page: 1 }));

  return dispatch(fetchFederalAgentContractAwards());
};

const fetchContractOpportunitiesWithParams =
  (params = {}) =>
  async (dispatch) => {
    const nextParams = params.contractOpportunities || params.opportunities || params;

    if (nextParams.quickFilter) {
      dispatch(setContractOpportunitiesParams({ quickFilter: nextParams.quickFilter }));
    }

    if (nextParams.text !== undefined) {
      dispatch(setContractOpportunitiesFilters({ text: nextParams.text || '' }));
    }

    if (nextParams.filters?.fields) {
      dispatch(setContractOpportunitiesFilters({ fields: nextParams.filters.fields || {} }));
    }

    if (nextParams.order) {
      dispatch(setContractOpportunitiesOrder(nextParams.order));
    }

    if (nextParams.pagination) {
      dispatch(setContractOpportunitiesPagination(nextParams.pagination));
    }

    return dispatch(fetchFederalAgentContractOpportunities(params));
  };

const changeContractOpportunitiesPage = (payload) => async (dispatch) => {
  dispatch(setContractOpportunitiesPagination({ page: extractPage(payload) }));
  return dispatch(fetchFederalAgentContractOpportunities());
};

const changeContractOpportunitiesRowPerPage = (payload) => async (dispatch) => {
  dispatch(
    setContractOpportunitiesPagination({
      page: 1,
      perPage: extractPerPage(payload),
    })
  );

  return dispatch(fetchFederalAgentContractOpportunities());
};

const changeContractOpportunitiesSearchText = (payload) => async (dispatch) => {
  dispatch(setContractOpportunitiesFilters({ text: extractSearchText(payload) }));
  dispatch(setContractOpportunitiesPagination({ page: 1 }));

  return dispatch(fetchFederalAgentContractOpportunities());
};

const toggleUserOption = createAsyncThunk('federalAgents/toggleUserOption', (params) => {
  return application.call(API.FEDERAL_AGENTS.TOGGLE_USER_OPTION, params);
});

const onUpdateNote = (params) => application.call('federalAgents.updateNotes', params);

export const actions = {
  fetchFederalAgent,
  fetchFederalAgentContractAwards,
  fetchFederalAgentContractOpportunities: fetchContractOpportunitiesWithParams,
  changeContractAwardsPage,
  changeContractAwardsRowPerPage,
  changeContractAwardsSearchText,
  changeContractAwardsOrder,
  changeContractOpportunitiesPage,
  changeContractOpportunitiesRowPerPage,
  changeContractOpportunitiesSearchText,
  toggleUserOption,
  onUpdateNote,
  naicsCodesListAtions,
  colleaguesListAtions,
  opportunitiesListAtions,
};

function onFetchFederalAgentFulfilled(state, action) {
  const contractAwards = normalizeListPayload(action.payload?.contractAwards);
  const contractOpportunities = normalizeListPayload(action.payload?.contractOpportunities);
  const originalLookupId = action.meta?.arg?.id;
  const fullContractAwards = contractAwards.allItems || contractAwards.items || [];
  const fullContractOpportunities = contractOpportunities.allItems || contractOpportunities.items || [];

  return Object.assign(state, {
    item: action.payload
      ? {
          ...action.payload,
          // Keep full awards/opportunities on the item for non-table consumers.
          contractAwards: fullContractAwards,
          contractOpportunities: fullContractOpportunities,
        }
      : null,
    lookupId: originalLookupId || action.payload?.email || action.payload?._id || action.payload?.id || state.lookupId,
    contractAwards: {
      ...state.contractAwards,
      ...contractAwards,
      pending: false,
    },
    contractOpportunities: {
      ...state.contractOpportunities,
      ...contractOpportunities,
      pending: false,
      params: {
        ...state.contractOpportunities.params,
        quickFilter: state.contractOpportunities.params?.quickFilter || 'active',
      },
    },
    pending: false,
  });
}

function onFetchFederalAgentContractAwardsPending(state) {
  return Object.assign(state, {
    contractAwards: {
      ...state.contractAwards,
      pending: true,
    },
  });
}

function onFetchFederalAgentContractAwardsFulfilled(state, action) {
  const contractAwards = normalizeListPayload(action.payload);

  return Object.assign(state, {
    // Do not overwrite item.contractAwards here.
    // Past Awards table updates should not change Funding Analysis input data.
    contractAwards: {
      ...state.contractAwards,
      ...contractAwards,
      allItems: contractAwards.allItems || state.contractAwards.allItems || [],
      pending: false,
    },
  });
}

function onFetchFederalAgentContractAwardsRejected(state) {
  return Object.assign(state, {
    contractAwards: {
      ...state.contractAwards,
      pending: false,
    },
  });
}

function onFetchFederalAgentContractOpportunitiesPending(state) {
  return Object.assign(state, {
    contractOpportunities: {
      ...state.contractOpportunities,
      pending: true,
    },
  });
}

function onFetchFederalAgentContractOpportunitiesFulfilled(state, action) {
  const contractOpportunities = normalizeListPayload(action.payload);

  return Object.assign(state, {
    contractOpportunities: {
      ...state.contractOpportunities,
      ...contractOpportunities,
      allItems: contractOpportunities.allItems || state.contractOpportunities.allItems || [],
      pending: false,
    },
  });
}

function onFetchFederalAgentContractOpportunitiesRejected(state) {
  return Object.assign(state, {
    contractOpportunities: {
      ...state.contractOpportunities,
      pending: false,
    },
  });
}

export default createReducer(INITIAL_STATE, (builder) => {
  extendBuilderWithListActions(builder, naicsCodesListAtions, (store) => store.naicsCodes);
  extendBuilderWithListActions(builder, colleaguesListAtions, (store) => store.colleagues);
  extendBuilderWithListActions(builder, opportunitiesListAtions, (store) => store.opportunities);

  return builder
    .addCase(fetchFederalAgent.fulfilled, onFetchFederalAgentFulfilled)
    .addCase(fetchFederalAgent.rejected, onReject)
    .addCase(fetchFederalAgent.pending, (state) => Object.assign(state, INITIAL_STATE, { pending: true }))

    .addCase(fetchFederalAgentContractAwards.pending, onFetchFederalAgentContractAwardsPending)
    .addCase(fetchFederalAgentContractAwards.fulfilled, onFetchFederalAgentContractAwardsFulfilled)
    .addCase(fetchFederalAgentContractAwards.rejected, onFetchFederalAgentContractAwardsRejected)

    .addCase(fetchFederalAgentContractOpportunities.pending, onFetchFederalAgentContractOpportunitiesPending)
    .addCase(fetchFederalAgentContractOpportunities.fulfilled, onFetchFederalAgentContractOpportunitiesFulfilled)
    .addCase(fetchFederalAgentContractOpportunities.rejected, onFetchFederalAgentContractOpportunitiesRejected)

    .addCase(setContractAwardsPagination, (state, action) =>
      Object.assign(state, {
        contractAwards: {
          ...state.contractAwards,
          pagination: {
            ...state.contractAwards.pagination,
            ...action.payload,
          },
        },
      })
    )
    .addCase(setContractAwardsFilters, (state, action) =>
      Object.assign(state, {
        contractAwards: {
          ...state.contractAwards,
          filters: {
            ...state.contractAwards.filters,
            ...action.payload,
          },
        },
      })
    )
    .addCase(setContractAwardsOrder, (state, action) =>
      Object.assign(state, {
        contractAwards: {
          ...state.contractAwards,
          order: action.payload,
        },
      })
    )

    .addCase(setContractOpportunitiesPagination, (state, action) =>
      Object.assign(state, {
        contractOpportunities: {
          ...state.contractOpportunities,
          pagination: {
            ...state.contractOpportunities.pagination,
            ...action.payload,
          },
        },
      })
    )
    .addCase(setContractOpportunitiesFilters, (state, action) =>
      Object.assign(state, {
        contractOpportunities: {
          ...state.contractOpportunities,
          filters: {
            ...state.contractOpportunities.filters,
            ...action.payload,
          },
        },
      })
    )
    .addCase(setContractOpportunitiesOrder, (state, action) =>
      Object.assign(state, {
        contractOpportunities: {
          ...state.contractOpportunities,
          order: action.payload,
        },
      })
    )
    .addCase(setContractOpportunitiesParams, (state, action) =>
      Object.assign(state, {
        contractOpportunities: {
          ...state.contractOpportunities,
          params: {
            ...state.contractOpportunities.params,
            ...action.payload,
          },
        },
      })
    );
});
